import { useState } from 'react';
import type { ReactElement } from 'react';

import {
  useCreateBusinessRuleMutation,
  useGetBusinessRulesQuery,
  useUpdateBusinessRuleMutation,
} from 'features/notification-config/services/businessRules.api';

import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

const WHATSAPP_FLOWS_RULE = 'WHATSAPP_FLOWS_ENABLED';
const SEND_EXECUTIVE_VCARD_RULE = 'SEND_EXECUTIVE_VCARD_ENABLED';

interface Props {
  projectId: number;
}

interface ToolToggleProps {
  projectId: number;
  ruleName: string;
  id: string;
  label: string;
  description: string;
  errorMessage: string;
}

/*
 * One boolean Business Rule per agent tool. Creating the row is enough to turn the tool on; deleting
 * it is not required — a false value is the same as "no rule" for the backend filter.
 */
const ToolToggle = ({ projectId, ruleName, id, label, description, errorMessage }: ToolToggleProps): ReactElement => {
  const { data } = useGetBusinessRulesQuery({
    name: ruleName,
    entityType: 'Project',
    entityId: projectId,
    exact: true,
    page: 1,
    limit: 1,
  });
  const [createRule, { isLoading: isCreating }] = useCreateBusinessRuleMutation();
  const [updateRule, { isLoading: isUpdating }] = useUpdateBusinessRuleMutation();
  const [error, setError] = useState<string | null>(null);

  const rule = data?.data?.[0] ?? null;
  const isEnabled = rule?.value === true;
  const isSaving = isCreating || isUpdating;

  const handleToggle = async (checked: boolean): Promise<void> => {
    setError(null);
    try {
      if (rule) {
        await updateRule({ id: rule.id, businessRule: { value: checked } }).unwrap();
      } else {
        await createRule({
          name: ruleName,
          entityType: 'Project',
          entityId: projectId,
          enabled: true,
          value: checked,
        }).unwrap();
      }
    } catch {
      setError(errorMessage);
    }
  };

  return (
    <div className="flex items-start justify-between gap-3 rounded-md border border-border p-3">
      <div className="space-y-1 pr-2">
        <Label htmlFor={id} className="text-sm font-medium">
          {label}
        </Label>
        <p className="text-xs text-text-muted">{description}</p>
        {error && (
          <p className="rounded-md border border-destructive bg-destructive/10 p-2 text-xs text-destructive">{error}</p>
        )}
      </div>
      <Switch
        id={id}
        checked={isEnabled}
        disabled={isSaving}
        onCheckedChange={(checked) => {
          void handleToggle(checked);
        }}
      />
    </div>
  );
};

/*
 * Tools the agent may use in this master, each one a business rule the backend reads to decide which
 * functions reach the model (filterToolsByBusinessRules). Every toggle here persists on change —
 * there is no draft to save, unlike the capabilities and parameters tabs.
 */
const MasterToolsSection = ({ projectId }: Props): ReactElement => {
  return (
    <section className="space-y-4 rounded-md border border-border p-6">
      <p className="text-xs text-text-muted">
        Cada herramienta se activa por inmobiliaria y cambia lo que el agente puede hacer en la conversación.
      </p>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <ToolToggle
          projectId={projectId}
          ruleName={WHATSAPP_FLOWS_RULE}
          id="whatsapp-flows-toggle"
          label="Formularios de WhatsApp"
          description="Permite que el agente envíe formularios interactivos. Cuáles puede enviar se elige por formulario."
          errorMessage="No se pudo actualizar los formularios de WhatsApp."
        />
        <ToolToggle
          projectId={projectId}
          ruleName={SEND_EXECUTIVE_VCARD_RULE}
          id="send-executive-vcard-toggle"
          label="vCard del ejecutivo"
          description="Permite que el agente envíe la vCard de WhatsApp del ejecutivo asignado para que el cliente la guarde."
          errorMessage="No se pudo actualizar el envío de vCard del ejecutivo."
        />
      </div>
    </section>
  );
};

export default MasterToolsSection;
